import moment from "moment";

/**
 * Shared time-range logic for sensor graph pages.
 *
 * Backend contract (fyllo-core):
 * - GET /sensor-data/{plotId} accepts `?sinceHours=` OR `?startTime=&endTime=`
 *   (explicit startTime wins). Enforced cap: 60 days via validateStartEndTime.
 * - GET /final-field-data accepts a LoopBack `?filter=` with
 *   `where.timestamp.gte/lte`.
 */

export type TimeRangeMode = "sinceHours" | "custom";

export interface TimeRangeSelection {
  mode: TimeRangeMode;
  /** Used when mode === 'sinceHours'. Hours before end (default: now). */
  sinceHours: number;
  /** ISO strings. Used when mode === 'custom'. */
  startTime?: string;
  endTime?: string;
}

/** Must stay in sync with fyllo-core validateStartEndTime limit (60 days). */
export const MAX_SENSOR_RANGE_DAYS = 60;
export const MAX_SINCE_HOURS = MAX_SENSOR_RANGE_DAYS * 24; // 1440
export const MIN_SINCE_HOURS = 1;
/** Above this, raw data gets heavy — warn instead of blocking. */
export const LARGE_RANGE_WARN_DAYS = 7;
/** Cap rows pulled through the generic /final-field-data filter. */
export const FINAL_FIELD_DATA_LIMIT = 5000;

export function toISO(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}

/**
 * ion-datetime (Ionic React 5, used by TimeRangeSelector) is timezone-naive: it renders
 * whatever Y/M/D/H/m digits are in the string it's given verbatim, with no UTC<->local
 * conversion. Feed it `.toISOString()` (UTC digits) and the picker silently shows/bounds
 * itself in UTC instead of the browser's local time (e.g. off by 5:30h for IST).
 * Local, offset-less digits are what make the picker match the user's actual wall clock.
 */
export function toLocalDateTimeString(value: Date): string {
  return moment(value).format("YYYY-MM-DDTHH:mm:ss");
}

/**
 * ion-datetime's onIonChange can echo back a trailing Z/offset inherited from the min/max/
 * value it was seeded with, even though the digits themselves are just the local wall-clock
 * numbers the user picked. Stripping it keeps every stored startTime/endTime offset-less, so
 * `new Date(...)` downstream (toISO, resolveTimeRange, validateCustomRange) parses those
 * digits as the browser's local time — matching what the user actually saw and picked —
 * instead of misreading them as true UTC.
 */
export function normalizePickedDateTime(raw: string): string {
  return raw.replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
}

export function validateSinceHours(sinceHours: number): string | null {
  if (sinceHours === null || sinceHours === undefined || Number.isNaN(Number(sinceHours))) {
    return "Please enter since hours.";
  }
  const n = Number(sinceHours);
  if (!Number.isFinite(n) || n < MIN_SINCE_HOURS) {
    return `Since hours must be at least ${MIN_SINCE_HOURS}.`;
  }
  if (n > MAX_SINCE_HOURS) {
    return `Since hours cannot exceed ${MAX_SINCE_HOURS} (${MAX_SENSOR_RANGE_DAYS} days). Use a custom range within ${MAX_SENSOR_RANGE_DAYS} days.`;
  }
  return null;
}

export function validateCustomRange(
  startTime?: string,
  endTime?: string,
  maxRangeDays: number = MAX_SENSOR_RANGE_DAYS
): string | null {
  if (!startTime) return "Please select a start date & time.";
  if (!endTime) return "Please select an end date & time.";
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Invalid date selected.";
  }
  if (end < start) {
    return "End time cannot be before start time.";
  }
  if (end.getTime() > Date.now()) {
    return "End time cannot be in the future.";
  }
  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays > maxRangeDays) {
    return `Date range cannot exceed ${maxRangeDays} days. Please narrow the range.`;
  }
  return null;
}

export function validateTimeRange(
  selection: TimeRangeSelection,
  maxRangeDays: number = MAX_SENSOR_RANGE_DAYS
): string | null {
  if (selection.mode === "sinceHours") {
    return validateSinceHours(selection.sinceHours);
  }
  return validateCustomRange(selection.startTime, selection.endTime, maxRangeDays);
}

export function isLargeRange(selection: TimeRangeSelection): boolean {
  if (selection.mode === "sinceHours") {
    return Number(selection.sinceHours) > LARGE_RANGE_WARN_DAYS * 24;
  }
  if (!selection.startTime || !selection.endTime) return false;
  const diffDays =
    (new Date(selection.endTime).getTime() - new Date(selection.startTime).getTime()) /
    (1000 * 60 * 60 * 24);
  return diffDays > LARGE_RANGE_WARN_DAYS;
}

/**
 * Resolve any selection to absolute [start, end] Dates.
 * sinceHours mode anchors end at `now` (previous behaviour).
 */
export function resolveTimeRange(selection: TimeRangeSelection, now: Date = new Date()): { start: Date; end: Date } {
  if (selection.mode === "custom" && selection.startTime && selection.endTime) {
    return { start: new Date(selection.startTime), end: new Date(selection.endTime) };
  }
  const end = now;
  const start = new Date(end.getTime() - Number(selection.sinceHours) * 60 * 60 * 1000);
  return { start, end };
}

/** Query string for GET /sensor-data/{plotId}. Prefers explicit range. */
export function buildSensorDataQuery(selection: TimeRangeSelection): string {
  if (selection.mode === "custom" && selection.startTime && selection.endTime) {
    return `?startTime=${encodeURIComponent(toISO(selection.startTime))}&endTime=${encodeURIComponent(
      toISO(selection.endTime)
    )}`;
  }
  return `?sinceHours=${Number(selection.sinceHours)}`;
}

/** LoopBack filter object for GET /final-field-data. Always bounded gte+lte. */
export function buildFinalFieldDataFilter(deviceId: string, selection: TimeRangeSelection) {
  const { start, end } = resolveTimeRange(selection);
  return {
    where: {
      and: [{ timestamp: { gte: start } }, { timestamp: { lte: end } }, { deviceId }],
    },
    order: "timestamp ASC",
    limit: FINAL_FIELD_DATA_LIMIT,
  };
}

export function formatRangeSummary(selection: TimeRangeSelection): string {
  const { start, end } = resolveTimeRange(selection);
  const fmt = "DD/MM/YYYY HH:mm";
  const hours = Math.round((end.getTime() - start.getTime()) / 3600000);
  return `${moment(start).format(fmt)} → ${moment(end).format(fmt)} (${hours}h)`;
}

/**
 * Upper bound for IonDatetime pickers — blocks future dates only. There's no lower
 * bound: the backend cap (MAX_SENSOR_RANGE_DAYS) limits the *span* between start and
 * end, not how far back either can be, so users can pick e.g. a 60-day window from
 * last year. Span is enforced separately by validateCustomRange on Apply.
 * Local wall-clock digits, see toLocalDateTimeString.
 */
export function getPickerMaxBound(): string {
  return toLocalDateTimeString(new Date());
}
